// src/oura/board/useBoard.ts
//
// Every client's Delivery, read for the board. There is no across-clients
// Delivery route yet, so the board reads the client list, then each client's
// Delivery, a few at a time. A client whose Delivery fails to load is named,
// never counted as on track.

import { useCallback, useEffect, useRef, useState } from "react";
import { listBusinessesWithCounts, type AeoBusiness } from "@service/aeo";
import { getDelivery, isDeliveryLive } from "@service/aeo/delivery";
import { myToday } from "../components/docs/boardModel";
import { boardClientOf, type BoardClient } from "./model";
import { sampleClients } from "./sample";
import { catchErrorLog } from "@util/sentry";

export type BoardLoad =
    | { state: "loading" }
    /** Delivery is not switched on in this build: no clients, real or invented. */
    | { state: "unavailable" }
    /** The read itself failed (the client list, or something unexpected). */
    | { state: "error" }
    | {
          state: "ready";
          clients: BoardClient[];
          /** Clients whose Delivery could not be read. */
          failed: { id: string; name: string }[];
          /** Signed clients with no Delivery yet. */
          noPlan: { id: string; name: string }[];
          sample: boolean;
          readAt: string;
      };

const AT_ONCE = 4;

/** A signed or running client: the board leaves prospects and ended plans out. */
function isClient(b: AeoBusiness): boolean {
    if (b.stage) return b.stage === "active" || b.stage === "onboarding";
    return b.state === "client";
}

async function readAll(): Promise<BoardLoad> {
    const readAt = new Date().toISOString();
    if (!isDeliveryLive) {
        // Sample clients on the dev server only. A build never shows invented clients.
        if (import.meta.env.DEV) return { state: "ready", clients: sampleClients(myToday()), failed: [], noPlan: [], sample: true, readAt };
        return { state: "unavailable" };
    }

    const { businesses } = await listBusinessesWithCounts();
    const queue = businesses.filter(isClient);
    const clients: BoardClient[] = [];
    const failed: { id: string; name: string }[] = [];
    const noPlan: { id: string; name: string }[] = [];
    let next = 0;
    const worker = async () => {
        while (next < queue.length) {
            const b = queue[next++];
            try {
                const r = await getDelivery(b.id, b.business_name);
                if (!r.ok) failed.push({ id: b.id, name: b.business_name });
                else if (!r.data) noPlan.push({ id: b.id, name: b.business_name });
                else clients.push(boardClientOf(r.data));
            } catch {
                // One client's read throwing never stops the rest; it is named as failed.
                failed.push({ id: b.id, name: b.business_name });
            }
        }
    };
    await Promise.all(Array.from({ length: Math.min(AT_ONCE, queue.length) }, worker));
    return { state: "ready", clients, failed, noPlan, sample: false, readAt };
}

export function useBoard() {
    const [load, setLoad] = useState<BoardLoad>({ state: "loading" });
    const seq = useRef(0);
    const reload = useCallback(() => {
        const mine = ++seq.current;
        void readAll()
            .then((r) => {
                if (mine === seq.current) setLoad(r);
            })
            .catch((err) => {
                catchErrorLog(`[oura] tracker read failed: ${err}`);
                if (mine === seq.current) setLoad({ state: "error" });
            });
    }, []);
    useEffect(() => {
        reload();
        return () => {
            seq.current++;
        };
    }, [reload]);
    return { load, reload };
}
